<template>
  <div class="club-edit-container">
    <!-- 載入狀態 -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
    </div>

    <!-- 編輯/新建表單 -->
    <div v-else-if="hasPermission" class="max-w-2xl mx-auto">
      <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow">
        <h1 class="text-2xl font-bold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-edit mr-3 text-wood-600"></i>
          {{ isCreateMode ? '建立新武場' : '編輯武場資料' }}
        </h1>

        <form @submit.prevent="saveClub" class="space-y-6">
          <!-- 武場代碼 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              武場代碼（限用英文或數字，將成為專屬連結）
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.uid"
              type="text"
              required
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
              placeholder="請輸入武場代碼"
            />
          </div>

          <!-- 武場名稱 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              武場名稱
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.name"
              type="text"
              required
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
              placeholder="請輸入武場名稱"
            />
          </div>

          <!-- 武場地址 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              武場地址
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.position"
              type="text"
              required
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
              placeholder="請輸入武場地址"
            />
          </div>

          <!-- 武場簡介 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              武場簡介
              <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="formData.note"
              required
              rows="4"
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
              placeholder="請輸入武場簡介"
            ></textarea>
          </div>

          <!-- 武場權限 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              武場權限
            </label>
            <select
              v-model="formData.permission"
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
            >
              <option value="">選擇武場權限</option>
              <option value="0">任何人皆可加入</option>
              <option value="1">需經過審核才可加入</option>
              <option value="2">完全封閉，邀請制</option>
            </select>
          </div>

          <!-- 附加圖片 -->
          <div class="form-group">
            <label class="block text-sm font-medium text-wood-700 mb-2">
              附加圖片
            </label>
            
            <!-- 現有圖片預覽 -->
            <div v-if="club && club.attachment" class="mb-4 text-center">
              <div class="text-sm text-wood-600 mb-2">
                現有圖片：{{ club.attachment.split('/').pop() }}
              </div>
              <img
                :src="club.attachment"
                alt="武場圖片"
                class="max-w-xs mx-auto rounded-lg shadow-md"
              />
            </div>

            <!-- 檔案選擇 -->
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              @change="handleFileChange"
              class="w-full px-4 py-2 border border-wood-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent bg-white/70"
            />
          </div>

          <!-- 錯誤訊息 -->
          <div v-if="errors.length" class="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 class="text-red-800 font-semibold mb-2">請修正以下問題：</h3>
            <ul class="text-red-700 space-y-1">
              <li v-for="error in errors" :key="error" class="flex items-center">
                <i class="fas fa-exclamation-circle mr-2"></i>
                {{ error }}
              </li>
            </ul>
          </div>

          <!-- 提交按鈕 -->
          <div class="flex justify-between pt-4">
            <button
              type="button"
              @click="goBack"
              class="px-6 py-2 bg-wood-300 hover:bg-wood-400 text-wood-800 rounded-lg transition-colors duration-200"
            >
              <i class="fas fa-arrow-left mr-2"></i>
              返回
            </button>
            
            <button
              type="submit"
              :disabled="submitting"
              class="px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-gray-800 border-1 border-gray-700 hover:border-gray-800 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i v-if="submitting" class="fas fa-spinner animate-spin mr-2"></i>
              <i v-else class="fas fa-save mr-2"></i>
              {{ submitting ? (isCreateMode ? '建立中...' : '儲存中...') : (isCreateMode ? '建立武場' : '儲存') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 未登入狀態 -->
    <div v-else-if="!userStore.isLoggedIn" class="text-center">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
        <i class="fas fa-sign-in-alt text-blue-500 text-3xl mb-4"></i>
        <h2 class="text-blue-800 font-semibold mb-2">需要登入</h2>
        <p class="text-blue-700 mb-4">您需要先登入才能編輯武場資料。</p>
        <div class="space-y-2">
          <button
            @click="userStore.googleLogin"
            class="block w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
          >
            <i class="fab fa-google mr-2"></i>
            使用 Google 登入
          </button>
          <button
            @click="goToClubsList"
            class="block w-full px-4 py-2 bg-wood-300 hover:bg-wood-400 text-wood-800 rounded-lg transition-colors duration-200"
          >
            <i class="fas fa-list mr-2"></i>
            回到武場列表
          </button>
        </div>
      </div>
    </div>

    <!-- 權限不足狀態 -->
    <div v-else-if="!hasPermission" class="text-center">
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
        <i class="fas fa-shield-alt text-yellow-500 text-3xl mb-4"></i>
        <h2 class="text-yellow-800 font-semibold mb-2">權限不足</h2>
        <p class="text-yellow-700 mb-4">您必須是此武場的管理員才能編輯武場資料。</p>
        <div class="space-y-2">
          <button
            @click="goBack"
            class="block w-full px-4 py-2 bg-wood-500 hover:bg-wood-600 text-white rounded-lg transition-colors duration-200"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            返回武場詳情
          </button>
          <button
            @click="goToClubsList"
            class="block w-full px-4 py-2 bg-wood-300 hover:bg-wood-400 text-wood-800 rounded-lg transition-colors duration-200"
          >
            <i class="fas fa-list mr-2"></i>
            回到武場列表
          </button>
        </div>
      </div>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else class="text-center">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
        <i class="fas fa-exclamation-triangle text-red-500 text-3xl mb-4"></i>
        <h2 class="text-red-800 font-semibold mb-2">載入失敗</h2>
        <p class="text-red-700">無法載入武場資料，請稍後再試。</p>
        <button
          @click="goBack"
          class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
        >
          返回
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useUserStore } from '../stores/user.js'
import { useNotificationStore } from '../stores/notification.js'

const props = defineProps({
  clubId: {
    type: Number,
    default: null  // 如果沒有 clubId 則為新建模式
  },
  club: {
    type: Object,
    default: null  // 可以直接傳入 club 物件（編輯模式）
  }
})

const emit = defineEmits(['club-created', 'club-updated', 'close'])

// 使用 Pinia store
const userStore = useUserStore()
const notificationStore = useNotificationStore()

// 狀態管理
const loading = ref(true)
const submitting = ref(false)
const club = ref(null)
const errors = ref([])
const fileInput = ref(null)
const selectedFile = ref(null)
const hasPermission = ref(false)

// 判斷是否為新建模式
const isCreateMode = computed(() => !props.clubId && !props.club)

// 表單資料
const formData = ref({
  uid: '',
  name: '',
  position: '',
  note: '',
  permission: ''
})

// 載入武場資料
const loadClubData = async () => {
  try {
    loading.value = true
    errors.value = [] // 清空錯誤訊息
    
    // 檢查是否已登入
    if (!userStore.isLoggedIn) {
      hasPermission.value = false
      loading.value = false
      return
    }
    
    // 新建模式
    if (isCreateMode.value) {
      console.log('新建模式，初始化空表單')
      hasPermission.value = true
      club.value = null
      // 重設表單為空值
      formData.value = {
        uid: '',
        name: '',
        position: '',
        note: '',
        permission: ''
      }
      loading.value = false
      return
    }
    
    // 編輯模式 - 如果直接傳入 club 物件
    if (props.club) {
      console.log('編輯模式，使用傳入的 club 物件')
      club.value = props.club
      hasPermission.value = true // 假設傳入 club 物件時已有權限
      fillFormData()
      loading.value = false
      return
    }
    
    // 編輯模式 - 從 API 載入資料
    if (!props.clubId) {
      console.error('編輯模式需要 clubId 或 club 物件')
      errors.value = ['無法確定要編輯的武場']
      loading.value = false
      return
    }
    
    console.log('從 API 載入武場資料，ID:', props.clubId)
    const response = await axios.get(`/clubs/${props.clubId}.json`)
    club.value = response.data.club
    
    // 檢查是否為管理員
    hasPermission.value = response.data.is_admin
    
    if (!hasPermission.value) {
      loading.value = false
      return
    }
    
    fillFormData()
    
    console.log('武場資料載入成功:', club.value)
    console.log('管理員權限:', hasPermission.value)
  } catch (error) {
    console.error('載入武場資料失敗:', error)
    
    // 只有在編輯模式下才顯示載入錯誤
    if (!isCreateMode.value) {
      // 檢查是否為 403 權限錯誤
      if (error.response && error.response.status === 403) {
        hasPermission.value = false
      } else {
        errors.value = ['載入武場資料失敗，請重新整理頁面']
      }
    }
  } finally {
    loading.value = false
  }
}

// 填入表單資料
const fillFormData = () => {
  if (club.value) {
    formData.value = {
      uid: club.value.uid || '',
      name: club.value.name || '',
      position: club.value.position || '',
      note: club.value.note || '',
      permission: club.value.permission !== undefined ? club.value.permission.toString() : ''
    }
  }
}

// 處理檔案選擇
const handleFileChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    selectedFile.value = file
    console.log('選擇的檔案:', file.name)
  }
}

// 儲存武場資料（新建或更新）
const saveClub = async () => {
  try {
    submitting.value = true
    errors.value = []
    
    // 建立 FormData 物件
    const formDataObj = new FormData()
    formDataObj.append('club[uid]', formData.value.uid)
    formDataObj.append('club[name]', formData.value.name)
    formDataObj.append('club[position]', formData.value.position)
    formDataObj.append('club[note]', formData.value.note)
    formDataObj.append('club[permission]', formData.value.permission)
    
    // 如果有選擇新檔案，加入到表單資料
    if (selectedFile.value) {
      formDataObj.append('club[attachment]', selectedFile.value)
    }
    
    // 取得 CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    let response
    if (isCreateMode.value) {
      // 新建武場
      response = await axios.post('/clubs', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })
      console.log('武場建立成功:', response.data)
      
      // 顯示成功通知
      if (response.data.message) {
        notificationStore.success(response.data.message)
      }
      
      // 觸發創建成功事件
      emit('club-created', response.data.club || response.data)
      // 自動關閉 Modal
      close()
    } else {
      // 更新武場
      const clubId = props.clubId || (props.club && props.club.id)
      response = await axios.patch(`/clubs/${clubId}`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })
      console.log('武場更新成功:', response.data)
      
      // 顯示成功通知
      if (response.data.message) {
        notificationStore.success(response.data.message)
      }
      
      // 觸發更新成功事件
      emit('club-updated', response.data.club || response.data)
      // 自動關閉 Modal
      close()
    }
    
  } catch (error) {
    console.error(isCreateMode.value ? '建立武場失敗:' : '更新武場失敗:', error)
    
    if (error.response && error.response.data && error.response.data.errors) {
      // 處理驗證錯誤
      const errorMessages = []
      for (const field in error.response.data.errors) {
        error.response.data.errors[field].forEach(message => {
          errorMessages.push(`${getFieldLabel(field)}: ${message}`)
        })
      }
      errors.value = errorMessages
      // 顯示錯誤通知
      notificationStore.error('資料驗證失敗，請檢查表單資料')
    } else {
      const errorMsg = isCreateMode.value ? '建立武場失敗，請檢查輸入資料後再試' : '更新武場資料失敗，請檢查輸入資料後再試'
      errors.value = [errorMsg]
      // 顯示錯誤通知
      notificationStore.error(errorMsg)
    }
  } finally {
    submitting.value = false
  }
}

// 將英文欄位名稱轉換為中文
const getFieldLabel = (field) => {
  const labels = {
    uid: '武場代碼',
    name: '武場名稱',
    position: '武場地址',
    note: '武場簡介',
    permission: '武場權限',
    attachment: '武場圖片'
  }
  return labels[field] || field
}


// 導航到武場詳情頁
const navigateToClubDetail = (clubId = null) => {
  const targetClubId = clubId || props.clubId || (props.club && props.club.id)
  const url = `/clubs/${targetClubId}`
  window.history.pushState(null, '', url)
  
  // 觸發路由變更事件
  const event = new PopStateEvent('popstate')
  window.dispatchEvent(event)
}

// 返回按鈕
const goBack = () => {
  if (isCreateMode.value) {
    goToClubsList()
  } else {
    navigateToClubDetail()
  }
}

// 導航到武場列表頁
const goToClubsList = () => {
  const url = '/clubs'
  window.history.pushState(null, '', url)
  
  // 觸發路由變更事件
  const event = new PopStateEvent('popstate')
  window.dispatchEvent(event)
}

// 關閉 Modal
const close = () => {
  emit('close')
}

// 組件掛載時載入資料
onMounted(() => {
  loadClubData()
})
</script>

<style scoped>
.club-edit-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dcc89a;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  box-shadow: 0 0 0 2px #a68b5b;
  border-color: transparent;
}

.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}
</style>